import riot from 'riot'
import logger from '../logger'
import extendedObservable from './base'
import {
  merge,
  findIndex,
  random,
  filter,
  extend,
  get,
} from 'lodash-es'


// /////////
// Modals //
// /////////

export function ModalFactoryObservable() {
  extendedObservable(this, 'modalFactorModel')

  /**
   * Array of active modals
   * @type {Array}
   */
  const activeModals = []

  /**
   * Creates a new modal and adds it to the list
   * of existing modals.
   * @param  {Object} opts options to pass into the
   *                       modal for creation
   * @return {null}      no return
   */
  const createModal = (opts) => {
    const modalPromise = new Promise((resolve, reject) => {
      if (activeModals.length === 0) {
        logger(`Creating modal: ${opts.modalId}`, 'info')()
        // eslint-disable-next-line
        opts = merge(opts, {});

        const opModal = document.createElement('op-modal')
        opModal.setAttribute('id', `modal_${opts.modalId}`)

        // Riot needs this to be evaluated so that when the component
        // mounts and is passed in opts, everything is wired up correctly.
        if (opts.template) {
          const template = document.createElement(opts.template)
          opModal.append(template)
        } else if (opts.content) {
          opModal.append(opts.content || '')
        }

        document.querySelector('body').append(opModal)
        const modal = riot.mount(`#modal_${opts.modalId}`, opts)

        // add the new tag instance to the active modals array
        activeModals.push(modal[0])
        opModal.querySelector('.modal').setAttribute('aria-hidden', false)

        logger('activeModals', 'debug')()
        logger(activeModals, 'debug')()
        resolve()
      } else {
        logger('Refused to create model. Existing modal still present', 'warn')()
        reject()
      }
    })

    return modalPromise
  }


  /**
   * Destroys a modal from a given tag instance (that
   * of the modal)
   * @param  {Object} component The modal component
   * @return {null}           no return
   */
  const destroyModal = component => new Promise((resolve) => {
    const toDeleteIndex = findIndex(activeModals, ['opts.modalId', component.opts.modalId])
    if (toDeleteIndex === -1) {
      logger(`Modal ${component.opts.modalId} already being destroyed `, 'info')()
      return
    }
    logger(`destroying modal ${component.opts.modalId}`, 'info')()

    activeModals.splice(toDeleteIndex, 1)
    component.unmount()
    resolve()
  })

  /**
   * Destroys all modals
   * @param  {Object} component The modal component
   * @return {null}           no return
   */
  const destroyModals = () => new Promise((resolve) => {
    activeModals.forEach((component) => {
      logger(`destroying modal ${component.opts.modalId}`, 'info')()
      component.unmount()
    })
    activeModals.splice(0, activeModals.length)

    resolve()
  })

  /**
   * Generate a random ID for a modal between 1 - 1000.
   * This should also guarantee that there is never a
   * collision with any existing ID assigned to a modal
   * @return {Integer} return a random integer
   */
  const generateId = () => {
    const id = random(1, 1000)
    const match = filter(activeModals, ['opts.modalId', id])
    return (match.length === 0) ? id : generateId()
  }

  // register and unregister event listeners
  this.on('registerModalFactoryObserver', observer => this.registerObserver(observer))
  this.on('unregisterModalFactoryObserver', observer => this.unregisterObserver(observer))

  /**
   * Event bus listener (can come from anywhere) that
   * will trigger the creation of a new modal. This will
   * trigger a new modal regardless if there is one on the
   * page already.
   */
  this.on('modalFactory.create', (opts) => {
    logger('trigger: creating modal', 'info')()
    const leopts = merge(opts, { lastActive: document.activeElement })
    createModal(extend({ modalId: generateId() }, leopts))
  })

  /**
   * Closes and destroys the modal indicated in the message.
   * This will likely come from something that "knows" about
   * the modal, like the modal's close button but in theory if
   * you had something listening to this observable we could
   * notify listeners upon creation and in turn those listeners
   * could delete the modal. For now the only thing aware of a
   * modal is the modal itself.
   */
  this.on('modalFactory.destroy', (component) => {
    if (get(component, 'opts.lastActive', false)) {
      setTimeout(() => component.opts.lastActive.focus(), 0)
    }
    destroyModal(component)
  })

  this.on('clearModals', (cb) => {
    destroyModals().then(cb)
  })
}
